import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ClipboardList, Video, BookOpen, Send, Check, Target, Users, ListChecks, Trash2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { EmptyState } from '../components/ui/EmptyState';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { useToast } from '../components/Toast';
import { formatStudentName } from '../utils/student';
import { groupAssignmentsForTeacher, formatDueDateForSave } from '../utils/assignment';
import type { ClassRow, AssignmentRow, ClassStudentRow } from '../types/sinif';

interface StudentWithAssignment {
  student_id: string;
  display_name: string | null;
  assignment?: AssignmentRow;
}

export default function ClassAssignments() {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useUser();
  const getSupabase = useSupabaseClient();
  const { showToast } = useToast();

  const [classRow, setClassRow] = useState<ClassRow | null>(null);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [students, setStudents] = useState<StudentWithAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Yeni görev formu
  const [type, setType] = useState<'video' | 'odev' | 'test'>('video');
  const [title, setTitle] = useState('');
  const [resourceRef, setResourceRef] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [assignAll, setAssignAll] = useState(true);

  const load = useCallback(async () => {
    if (!classId || !user) return;
    const supabase = await getSupabase();

    const { data: classData } = await supabase.from('classes').select('*').eq('id', classId).single();
    setClassRow(classData as ClassRow | null);

    const { data: memberRows } = await supabase
      .from('class_students')
      .select('*')
      .eq('class_id', classId)
      .eq('status', 'aktif');
    const members = (memberRows as ClassStudentRow[]) ?? [];
    const studentIds = members.map(m => m.student_id);

    let nameMap = new Map<string, string>();
    if (studentIds.length > 0) {
      const [{ data: snaps }, { data: profiles }] = await Promise.all([
        supabase.from('student_snapshots').select('student_id, display_name').in('student_id', studentIds),
        supabase.from('profiles').select('user_id, full_name').in('user_id', studentIds),
      ]);

      const snapMap = new Map((snaps ?? []).map((s: any) => [s.student_id, s.display_name]));
      const profileMap = new Map((profiles ?? []).map((p: any) => [p.user_id, p.full_name]));

      for (const sid of studentIds) {
        const sName = snapMap.get(sid);
        const pName = profileMap.get(sid);
        const raw = sName && !sName.startsWith('user_') ? sName : pName ?? sName;
        nameMap.set(sid, formatStudentName(raw, 'Öğrenci'));
      }
    }

    const { data: assignmentRows } = await supabase
      .from('assignments')
      .select('*')
      .eq('teacher_id', user.id)
      .eq('class_id', classId)
      .order('created_at', { ascending: false });
    const assignmentList = (assignmentRows as AssignmentRow[]) ?? [];
    setAssignments(assignmentList);

    // Her öğrenci için son atamayı eşleştir
    const enriched: StudentWithAssignment[] = members.map(m => ({
      student_id: m.student_id,
      display_name: nameMap.get(m.student_id) ?? null,
      assignment: assignmentList.find(a => a.student_id === m.student_id),
    }));
    setStudents(enriched);
    setIsLoading(false);
  }, [classId, user, getSupabase]);

  useEffect(() => { load(); }, [load]);

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !classId || !title.trim()) return;
    const targets = assignAll ? students.map(s => s.student_id) : selectedStudentIds;
    if (targets.length === 0) return;
    setIsSubmitting(true);
    const supabase = await getSupabase();
    const rows = targets.map(sid => ({
      teacher_id: user.id,
      student_id: sid,
      class_id: classId,
      type,
      title: title.trim(),
      resource_ref: resourceRef.trim() || null,
      due_date: formatDueDateForSave(dueDate),
    }));
    const { error } = await supabase.from('assignments').insert(rows);
    if (!error) {
      setTitle('');
      setResourceRef('');
      setDueDate('');
      setSelectedStudentIds([]);
      load();
    }
    setIsSubmitting(false);
  }

  function toggleStudent(id: string) {
    setSelectedStudentIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  }

  async function handleDeleteAssignmentGroup(assignmentIds: string[], title: string) {
    if (!window.confirm(`"${title}" başlıklı ödev tüm öğrencilerden silinecek. Emin misiniz?`)) return;
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('assignments').delete().in('id', assignmentIds);
      if (error) throw error;
      showToast('Ödev başarıyla geri çekildi/silindi.', 'success');
      load();
    } catch (err) {
      console.error('Ödev silme hatası:', err);
      showToast('Ödev silinirken bir hata oluştu.', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-10 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[0, 1, 2].map(i => (
            <div key={i} className="rounded-2xl h-[76px] animate-shimmer" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="rounded-2xl h-[360px] animate-shimmer" />
          <div className="lg:col-span-2 flex flex-col gap-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="rounded-2xl h-[68px] animate-shimmer" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (!classRow) return <div className="p-10 text-text-muted">Sınıf bulunamadı.</div>;

  // Özet istatistikler
  const groupedAssignments = groupAssignmentsForTeacher(assignments);
  const totalAssignments = groupedAssignments.length;
  const completedCount = assignments.filter(a => a.status === 'tamamlandi').length;
  const pendingCount = assignments.filter(a => a.status !== 'tamamlandi').length;

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-5xl mx-auto">
      <Helmet>
        <title>{classRow.name} — Görev Yönetimi | Bitmiyo Sınıf</title>
        <meta name="description" content="Sınıfa özel toplu video ve ödev atama paneli." />
      </Helmet>

      {/* Geri */}
      <Link
        to={`/sinif/${classId}`}
        className="inline-flex items-center gap-1.5 text-text-muted text-sm mb-5 hover:text-accent-600 transition-colors"
      >
        <ArrowLeft size={14} /> Sınıfa dön
      </Link>

      {/* Başlık */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl md:text-3xl font-black text-text-primary" style={{ fontFamily: 'var(--font-heading)' }}>
          Görev Yönetimi
        </h1>
        <span className="text-sm text-text-muted font-semibold">— {classRow.name}</span>
      </div>

      {/* Özet stat kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card className="p-4 text-center animate-fade-in-up" style={{ animationDelay: '0ms' }}>
          <p className="text-3xl font-black text-text-primary mb-1">{totalAssignments}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Toplam Görev</p>
        </Card>
        <Card className="p-4 text-center animate-fade-in-up" style={{ animationDelay: '60ms' }}>
          <p className="text-3xl font-black text-success-500 mb-1">{completedCount}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Tamamlanan</p>
        </Card>
        <Card className="p-4 text-center animate-fade-in-up" style={{ animationDelay: '120ms' }}>
          <p className="text-3xl font-black text-warning-500 mb-1">{pendingCount}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Bekleyen</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Sol: Görev Ata Formu */}
        <div className="lg:col-span-1">
          <h2 className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3">
            <Send size={12} /> Yeni Görev Ata
          </h2>
          <Card className="p-5">
            <form onSubmit={handleAssign} className="flex flex-col gap-3">
              {/* Tip */}
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  onClick={() => setType('video')}
                  className={`p-2 rounded-xl border text-xs font-bold transition-all active:scale-95 ${type === 'video' ? 'border-accent-500 bg-accent-100 text-accent-600' : 'border-border text-text-secondary hover:border-border/60'}`}
                >
                  Video
                </button>
                <button
                  type="button"
                  onClick={() => setType('test')}
                  className={`p-2 rounded-xl border text-xs font-bold transition-all active:scale-95 ${type === 'test' ? 'border-accent-500 bg-accent-100 text-accent-600' : 'border-border text-text-secondary hover:border-border/60'}`}
                >
                  Test Ödevi
                </button>
                <button
                  type="button"
                  onClick={() => setType('odev')}
                  className={`p-2 rounded-xl border text-xs font-bold transition-all active:scale-95 ${type === 'odev' ? 'border-accent-500 bg-accent-100 text-accent-600' : 'border-border text-text-secondary hover:border-border/60'}`}
                >
                  Ödev
                </button>
              </div>

              {/* Başlık */}
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Görev başlığı"
                className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-accent-400 transition-colors"
              />
              <input
                value={resourceRef}
                onChange={e => setResourceRef(e.target.value)}
                placeholder={
                  type === 'test'
                    ? 'Test Kodu veya Linki (ör. X7Y9Z2 veya https://soru.bitmiyo.com.tr/coz/...)'
                    : type === 'video'
                    ? 'YouTube linki'
                    : 'Ödev açıklaması'
                }
                className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-accent-400 transition-colors"
              />
              {type === 'test' && (
                <p className="text-[11px] text-accent-600 dark:text-accent-400 font-medium -mt-1">
                  Öğrenci bu göreve tıkladığında doğrudan <strong>soru.bitmiyo.com.tr</strong> üzerindeki testi çözer.
                </p>
              )}
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-accent-400 transition-colors"
              />

              {/* Hedef seçimi */}
              <div className="border-t border-border pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="assign-all"
                    checked={assignAll}
                    onChange={e => setAssignAll(e.target.checked)}
                    className="w-4 h-4 rounded border-border text-accent-500"
                  />
                  <label htmlFor="assign-all" className="text-sm font-bold text-text-primary cursor-pointer">
                    Tüm sınıfa ({students.length} öğrenci)
                  </label>
                </div>
                {!assignAll && students.length > 0 && (
                  <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                    {students.map(s => (
                      <label key={s.student_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-border/30 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedStudentIds.includes(s.student_id)}
                          onChange={() => toggleStudent(s.student_id)}
                          className="w-4 h-4 rounded border-border text-accent-500"
                        />
                        <Avatar name={s.display_name} size={24} />
                        <span className="text-sm font-semibold text-text-primary truncate">
                          {s.display_name ?? s.student_id}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !title.trim() || (!assignAll && selectedStudentIds.length === 0)}
              >
                <Send size={15} />
                {isSubmitting ? 'Atanıyor...' : 'Görevi Ata'}
              </Button>
            </form>
          </Card>
        </div>

        {/* Sağ: Öğrenci + Son Görev Durumu */}
        <div className="lg:col-span-2">
          <h2 className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3">
            <Users size={12} /> Son Durum — Öğrenciler
          </h2>

          {students.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="Henüz öğrenci yok"
              description="Sınıfa öğrenci katıldığında burada görünecek."
              action={
                <Button as={Link} to={`/sinif/${classId}`} variant="outline">
                  <ArrowLeft size={15} /> Sınıf Detayına Git
                </Button>
              }
            />
          ) : (
            <div className="flex flex-col gap-2">
              {students.map((s, idx) => {
                const a = s.assignment;
                return (
                  <Card
                    as={Link}
                    to={`/sinif/${classId}/ogrenci/${s.student_id}`}
                    key={s.student_id}
                    className="p-4 flex items-center gap-4 hover:border-accent-400 hover:-translate-y-px active:scale-[0.99] transition-all animate-fade-in-up"
                    style={{ animationDelay: `${Math.min(idx, 10) * 35}ms` }}
                  >
                    <Avatar name={s.display_name} size={40} />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-text-primary truncate">
                        {s.display_name ?? s.student_id}
                      </p>
                      {a ? (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {a.type === 'video' ? (
                            <Video size={12} className="text-accent-500 shrink-0" />
                          ) : a.type === 'test' ? (
                            <Target size={12} className="text-purple-500 shrink-0" />
                          ) : (
                            <BookOpen size={12} className="text-text-muted shrink-0" />
                          )}
                          <p className="text-xs text-text-secondary truncate">{a.title}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-text-muted mt-0.5">Henüz görev atanmadı</p>
                      )}
                    </div>
                    {a ? (
                      <Badge variant={a.status === 'tamamlandi' ? 'success' : a.status === 'devam_ediyor' ? 'accent' : 'neutral'}>
                        {a.status === 'tamamlandi' ? 'Tamamlandı' : a.status === 'devam_ediyor' ? 'Devam Ediyor' : 'Atandı'}
                      </Badge>
                    ) : (
                      <Badge variant="neutral">Görev Yok</Badge>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Son Atamalar Listesi */}
          {groupedAssignments.length > 0 && (
            <div className="mt-8">
              <h2 className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3">
                <ListChecks size={12} /> Tüm Atamalar ({groupedAssignments.length})
              </h2>
              <div className="flex flex-col gap-2">
                {groupedAssignments.slice(0, 20).map((a, idx) => (
                  <Card
                    key={a.id}
                    className="p-3.5 flex items-center gap-3 animate-fade-in-up"
                    style={{ animationDelay: `${Math.min(idx, 10) * 30}ms` }}
                  >
                    <div className={`shrink-0 ${
                      a.type === 'video' ? 'text-accent-600' : a.type === 'test' ? 'text-purple-600' : 'text-text-secondary'
                    }`}>
                      {a.type === 'video' ? <Video size={20} /> : a.type === 'test' ? <Target size={20} /> : <ClipboardList size={20} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-text-primary truncate">{a.title}</p>
                      <p className="text-xs text-text-muted">
                        {a.due_date ? `Teslim: ${new Date(a.due_date).toLocaleDateString('tr-TR')}` : 'Süresiz'}
                        {a.totalStudents > 1 && ` • ${a.totalStudents} Öğrenci`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={a.completedStudents === a.totalStudents && a.totalStudents > 0 ? 'success' : a.completedStudents > 0 ? 'accent' : 'neutral'}>
                        {a.completedStudents === a.totalStudents && a.totalStudents > 0
                          ? <><Check size={10} /> Tamamlandı</>
                          : `${a.completedStudents}/${a.totalStudents} Tamamlandı`}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => handleDeleteAssignmentGroup(a.allAssignmentIds, a.title)}
                        className="p-1.5 rounded-lg text-danger-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-950/40 transition-colors"
                        title="Ödevi Geri Çek / Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </Card>
                ))}
                {groupedAssignments.length > 20 && (
                  <p className="text-xs text-text-muted text-center py-2">
                    +{groupedAssignments.length - 20} görev daha
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
